import { useEffect, useState } from 'react';
import { Table, Button, Empty, Tag, Tooltip, Space, Modal } from 'antd';
import {
  EyeOutlined,
  ReloadOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { internalAccounts } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import {
  CompanyCell,
  ContactCell,
  PlanCell
} from '../../components/internalAdmin/AccountCells';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';

// Stage 5N-2C: suspended-accounts page. Reactivate when the operator
// has accounts.reactivate (super_admin only). No suspend action here
// — these are already suspended.

export default function SuspendedAccountsPage() {
  const { hasPermission } = useInternalAdminAuth();
  const { notifyError, notifySuccess } = useAppNotifications();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });

  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const canReactivate = hasPermission('accounts.reactivate');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const resp = await internalAccounts.list({ status: 'suspended', limit: 50 });
      setData({
        items: Array.isArray(resp.items) ? resp.items : [],
        total: typeof resp.total === 'number' ? resp.total : 0
      });
    } catch (err) {
      setError(err);
      notifyError(err, { title: 'Could not load suspended accounts' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function confirmReactivate() {
    if (!reactivateTarget) return;
    setBusy(true);
    try {
      await internalAccounts.reactivate(reactivateTarget.tenantId);
      notifySuccess(
        'Account reactivated',
        `${reactivateTarget.legalName || reactivateTarget.slug} is active again.`
      );
      setReactivateTarget(null);
      load();
    } catch (err) {
      notifyError(err, { title: 'Could not reactivate account' });
    } finally {
      setBusy(false);
    }
  }

  const columns = [
    { title: 'Company', key: 'company', width: 220, ellipsis: true,
      render: (_v, row) => <CompanyCell row={row} /> },
    { title: 'GSTIN', dataIndex: 'gstin', key: 'gstin', width: 150,
      render: (v) => v ? <code className="text-[11px] text-ink-700">{v}</code> : <span className="text-ink-400">—</span> },
    { title: 'Contact', key: 'contact', width: 220, ellipsis: true,
      render: (_v, row) => <ContactCell row={row} /> },
    { title: 'Status', key: 'status', width: 120,
      render: () => <Tag color="red">suspended</Tag> },
    { title: 'Plan', key: 'plan', width: 130,
      render: (_v, row) => <PlanCell row={row} /> },
    { title: 'Actions', key: 'actions', width: 200, fixed: 'right',
      render: (_v, row) => (
        <Space size={4} wrap={false}>
          <Button size="small" icon={<EyeOutlined />}
            onClick={() => navigate(`/internal/accounts/${row.tenantId}`)}>
            View
          </Button>
          <Tooltip title={canReactivate ? '' : 'You do not have accounts.reactivate'}>
            <Button size="small" type="primary" icon={<PlayCircleOutlined />}
              disabled={!canReactivate}
              onClick={() => setReactivateTarget(row)}>
              Reactivate
            </Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader
        title="Suspended Accounts"
        subtitle="Tenants currently blocked from signing in. Reactivate when the review issue is cleared."
        actions={
          <Space>
            <Tag color="red" className="!text-sm !px-3 !py-1">{data.total} suspended</Tag>
            <Button icon={<ReloadOutlined />} onClick={load} disabled={loading}>Refresh</Button>
          </Space>
        }
      />

      {loading ? <LoadingBlock tip="Loading suspended accounts…" /> : (
        <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
          <Table
            rowKey="tenantId"
            dataSource={data.items}
            columns={columns}
            size="middle"
            pagination={false}
            scroll={{ x: 1140 }}
            tableLayout="fixed"
            locale={{ emptyText: error
              ? <div className="py-12 text-center text-ink-500 text-sm">Could not load suspended accounts.</div>
              : <div className="py-10">
                  <Empty
                    description={
                      <div className="text-ink-700">
                        <p className="text-sm font-semibold m-0">No suspended accounts</p>
                        <p className="text-xs text-ink-500 m-0 mt-1">
                          Tenants you suspend will appear here for reactivation.
                        </p>
                      </div>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
            }}
          />
        </div>
      )}

      <Modal
        open={Boolean(reactivateTarget)}
        title="Reactivate account?"
        okText="Reactivate account"
        okButtonProps={{ type: 'primary', loading: busy }}
        cancelButtonProps={{ disabled: busy }}
        onOk={confirmReactivate}
        onCancel={() => (busy ? null : setReactivateTarget(null))}
        maskClosable={!busy}
      >
        <p className="m-0">
          Reactivating <strong>{reactivateTarget && (reactivateTarget.legalName || reactivateTarget.slug)}</strong> will let the master user sign in again.
        </p>
      </Modal>
    </div>
  );
}
